import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Alert,
  Box,
} from '@mui/material';
import { Currency } from '../types/api';
import { householdsService } from '../services/households-service';
import { useCurrencyStore } from '../stores/currency-store';
import {
  SUPPORTED_CURRENCIES,
  CURRENCY_LABELS,
  formatCurrency,
} from '../utils/currency';

/**
 * Lets the household choose the currency its TOTALS are reported in.
 *
 * Changing this does not touch a single stored figure. Every ledger line keeps
 * the currency it was recorded in and the FX rate that applied on its own date,
 * which is what makes historical net worth reproducible. This setting only
 * decides which currency the sums are presented in.
 */
export const BaseCurrencyCard: React.FC = () => {
  const baseCurrency = useCurrencyStore((s) => s.baseCurrency);
  const householdId = useCurrencyStore((s) => s.householdId);
  const setBaseCurrency = useCurrencyStore((s) => s.setBaseCurrency);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleChange = async (next: Currency) => {
    if (next === baseCurrency) return;

    const previous = baseCurrency;
    setError(null);
    setSaved(false);

    if (!householdId) {
      setError('Still loading your household — try again in a moment.');
      return;
    }

    // Applied locally first so the totals relabel immediately; rolled back
    // below if the server rejects it. A setting that appears to take effect
    // and silently did not is the failure worth avoiding here.
    setBaseCurrency(next);
    setSaving(true);
    try {
      await householdsService.updateHousehold(householdId, { base_currency: next });
      setSaved(true);
    } catch (e) {
      setBaseCurrency(previous);
      setError(
        e instanceof Error
          ? `Could not save: ${e.message}`
          : 'Could not save your base currency.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ maxWidth: 600, mb: 3 }}>
      <CardHeader title="Base Currency" />
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Accounts always display in their own currency. This is the currency
          your <strong>totals</strong> are converted into — net worth, assets,
          investments and debts.
        </Typography>

        <FormControl fullWidth size="small" disabled={saving}>
          <InputLabel id="base-currency-label">Report totals in</InputLabel>
          <Select
            labelId="base-currency-label"
            label="Report totals in"
            value={baseCurrency}
            onChange={(e) => handleChange(e.target.value as Currency)}
          >
            {SUPPORTED_CURRENCIES.map((c) => (
              <MenuItem key={c} value={c}>
                {c} — {CURRENCY_LABELS[c]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Totals will read {formatCurrency(1234.56, baseCurrency, { base: baseCurrency })}.
            Past figures are not recalculated: each transaction keeps the
            exchange rate that applied on its own date.
          </Typography>
        </Box>

        {saved && !error && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Base currency set to {baseCurrency}.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
